'use client';

import { useCallback, useEffect, useRef, useState, useTransition } from 'react';

import { isRedirectError } from 'next/dist/client/components/redirect-error';

import { Button } from '@kit/ui/button';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import { finalizeSignupFieldsAction } from '../_lib/server/profile-setup-actions';

export function FinalizeSignupFields({
  nextPath,
  onComplete,
}: {
  nextPath: string;
  onComplete?: () => void;
}) {
  const started = useRef(false);
  const [failed, setFailed] = useState(false);
  const [pending, startTransition] = useTransition();
  const finish = useCallback(() => {
    startTransition(async () => {
      try {
        const result = await finalizeSignupFieldsAction({
          nextPath,
          ...(onComplete ? { redirectAfterSave: false } : {}),
        });
        if (result?.success) {
          setFailed(false);
          onComplete?.();
        }
      } catch (error) {
        if (isRedirectError(error)) throw error;
        setFailed(true);
      }
    });
  }, [nextPath, onComplete]);

  // Verification establishes the session in a separate request. Resume its
  // durable draft through a mutation action, never during Server Component render.
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    finish();
  }, [finish]);

  return (
    <div className="flex flex-col items-center gap-4" aria-live="polite">
      {failed && !pending ? (
        <>
          <p role="alert">
            <Trans
              i18nKey="auth:profileSaveFailed"
              defaults="Unable to save your details. Please try again."
            />
          </p>
          <Button
            onClick={finish}
            disabled={pending}
            data-test="signup-fields-retry"
          >
            <Trans i18nKey="common:retry" defaults="Try again" />
          </Button>
        </>
      ) : (
        <>
          <Spinner />
          <p>
            <Trans
              i18nKey="auth:profileFinishing"
              defaults="Finishing your registration…"
            />
          </p>
        </>
      )}
    </div>
  );
}
